import React, { FC } from "react";

import { ConfirmReturnContextProvider } from "~/context/ConfirmReturnContext/ConfirmReturnContext";
import { CustomerDataContextProvider } from "~/context/CustomerDataContext/CustomerDataContext";
import { FormatContextProvider } from "~/context/FormatContext/FormatContext";
import { LanguageContextProvider } from "~/context/LanguageContext/LanguageContext";
import { LoginStateContextProvider } from "~/context/LoginStateContext/LoginStateContext";
import { MerchantSettingsContextProvider } from "~/context/MerchantSettingsContext/MerchantSettingsContext";
import { ReturnContextProvider } from "~/context/ReturnContext/ReturnContext";
import { ReturnMethodsContextProvider } from "~/context/ReturnMethodsContext/ReturnMethodsContext";
import { ShipmentContextProvider } from "~/context/ShipmentContext/ShipmentContext";
import { ThemeContextProvider } from "~/context/ThemeContext/ThemeContext";
import { CartVisibilityContextProvider } from "~/context/CartVisibilityContext/CartVisibilityContext";
import { CollectionsNavigationVisibilityContextProvider } from "~/context/CollectionsNavigationVisibilityContext/CollectionsNavigationVisibilityContext";
import { useMerchantSettings } from "~/hooks/useMerchantSettings/useMerchantSettings";
import { useOrderDetails } from "~/hooks/useOrderDetails/useOrderDetails";
import { useSessionStorage } from "~/hooks/useSessionStorage/useSessionStorage";
import { PayPalProvider } from "../PayPalProvider/PayPalProvider";

export const AppProviders: FC = ({ children }) => {
  const {
    data: {
      viewSettings,
      merchant: { currency: storeCurrency },
      locales,
    },
  } = useMerchantSettings();
  const { primaryColor, secondaryColor, logoUrl } = viewSettings;
  const theme = {
    primaryColor,
    secondaryColor,
    logoUrl,
  };

  const [orderId, setOrderId] = useSessionStorage("orderId", "");
  const [customerEmail, setCustomerEmail] = useSessionStorage(
    "customerEmail",
    ""
  );
  const [loginSuccess, setLoginSuccess] = useSessionStorage(
    "loginSuccess",
    false
  );
  // The login form writes orderId/customerEmail per keystroke; the app-level
  // order lookup must key on the credentials captured at login completion,
  // not on live form input. The flow rides along so a stored session only
  // restores into the portal flow it logged in for.
  const [loginCredentials, setLoginCredentials] = useSessionStorage(
    "loginCredentials",
    {
      orderId: "",
      customerEmail: "",
      flow: "",
    }
  );

  const completeLogin = (
    orderId: string,
    customerEmail: string,
    flow: string
  ) => {
    setLoginCredentials({ orderId, customerEmail, flow });
    setLoginSuccess(true);
  };

  const {
    data: {
      order: { currency: localCurrency },
    },
  } = useOrderDetails(
    // This consumer enables only after loginSuccess, so the captcha tier can
    // never challenge it during the login flow (a successful lookup resets
    // the tier). A session restore under an already-armed tier falls back to
    // storeCurrency — accepted, cosmetic, self-heals on the next full login.
    {
      orderId: loginCredentials.orderId,
      customerEmail: loginCredentials.customerEmail,
    },
    () => {},
    loginSuccess &&
      loginCredentials.orderId !== "" &&
      loginCredentials.customerEmail !== ""
  );

  const currency = localCurrency || storeCurrency;

  return (
    <ThemeContextProvider theme={theme}>
      <MerchantSettingsContextProvider>
        <LanguageContextProvider merchantLanguages={locales}>
          <FormatContextProvider merchantCurrency={currency}>
            <LoginStateContextProvider
              orderId={orderId}
              setOrderId={setOrderId}
              customerEmail={customerEmail}
              setCustomerEmail={setCustomerEmail}
              setLoginSuccess={setLoginSuccess}
              loginSuccess={loginSuccess}
              loginCredentials={loginCredentials}
              completeLogin={completeLogin}
            >
              <ReturnContextProvider>
                <ReturnMethodsContextProvider>
                  <CustomerDataContextProvider>
                    <ShipmentContextProvider>
                      <ConfirmReturnContextProvider>
                        <PayPalProvider>
                          <CartVisibilityContextProvider>
                            <CollectionsNavigationVisibilityContextProvider>
                              {children}
                            </CollectionsNavigationVisibilityContextProvider>
                          </CartVisibilityContextProvider>
                        </PayPalProvider>
                      </ConfirmReturnContextProvider>
                    </ShipmentContextProvider>
                  </CustomerDataContextProvider>
                </ReturnMethodsContextProvider>
              </ReturnContextProvider>
            </LoginStateContextProvider>
          </FormatContextProvider>
        </LanguageContextProvider>
      </MerchantSettingsContextProvider>
    </ThemeContextProvider>
  );
};
